import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Hotel as HotelIcon, Plus, ArrowLeft, ArrowRight, X } from "lucide-react";
import { PackageData } from "@/pages/CreatePackage";
import { useHotels } from "@/hooks/useHotels";
import { Hotel as HotelType, HotelFormData } from "@/types/hotel";
import { HotelDialog } from "@/components/hotels/HotelDialog";
import { toast } from "sonner";
import { PackageHotelsList } from "./HotelStep/PackageHotelsList";
import { HotelFilters } from "./HotelStep/HotelFilters";
import { HotelsGrid } from "./HotelStep/HotelsGrid";
import { HotelStayDetails } from "./HotelStep/HotelStayDetails";
import { ProgressSteps } from "./ProgressSteps";
interface HotelStepProps {
  data: PackageData;
  onUpdate: (data: Partial<PackageData>) => void;
  onNext: () => void;
  onPrev: () => void;
  completedSteps?: number[];
  onStepClick?: (step: number) => void;
  isStepRequired?: (step: number) => boolean;
}
export const HotelStep = ({
  data,
  onUpdate,
  onNext,
  onPrev,
  completedSteps = [],
  onStepClick = () => {},
  isStepRequired = () => true
}: HotelStepProps) => {
  const {
    hotels,
    rooms,
    loading,
    saveHotel
  } = useHotels();
  const [packageHotels, setPackageHotels] = useState(data.hotels);
  const [isHotelDialogOpen, setIsHotelDialogOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedHotel, setSelectedHotel] = useState<HotelType | null>(null);
  const countries = useMemo(() => [...new Set(hotels.map(hotel => hotel.country))].sort(), [hotels]);
  const cities = useMemo(() => selectedCountry ? [...new Set(hotels.filter(hotel => hotel.country === selectedCountry).map(hotel => hotel.city))].sort() : [], [hotels, selectedCountry]);
  const filteredHotels = useMemo(() => {
    return hotels.filter(hotel => (!selectedCountry || hotel.country === selectedCountry) && (!selectedCity || hotel.city === selectedCity));
  }, [hotels, selectedCountry, selectedCity]);
  const handleSaveNewHotel = async (formData: HotelFormData, editingHotel: HotelType | null, imageFile: File | null) => {
    const success = await saveHotel(formData, editingHotel, imageFile);
    if (success) {
      toast.success("تمت إضافة الفندق بنجاح. يمكنك الآن اختياره من القائمة.");
      setIsHotelDialogOpen(false);
    }
    return success;
  };
  const handleAddHotelToPackage = (newHotelStay: PackageData['hotels'][0]) => {
    const updatedHotels = [...packageHotels, newHotelStay];
    setPackageHotels(updatedHotels);
    onUpdate({
      hotels: updatedHotels
    });
    setSelectedHotel(null);
  };
  const handleRemoveHotel = (index: number) => {
    const updatedHotels = packageHotels.filter((_, i) => i !== index);
    setPackageHotels(updatedHotels);
    onUpdate({
      hotels: updatedHotels
    });
  };
  useEffect(() => {
    setSelectedCity('');
    setSelectedHotel(null);
  }, [selectedCountry]);
  useEffect(() => {
    setSelectedHotel(null);
  }, [selectedCity]);
  return <>
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HotelIcon className="w-5 h-5" />
            الفنادق والإقامة
          </CardTitle>
          
        </CardHeader>
        <CardContent className="space-y-6">
          {loading && <div className="text-center">جاري تحميل بيانات الفنادق...</div>}

          <PackageHotelsList packageHotels={packageHotels} onRemoveHotel={handleRemoveHotel} />

          <div className="pt-6 border-t">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-800">
                {!selectedHotel ? 'اختر فندقاً أو أضف فندقاً جديداً' : 'تفاصيل الإقامة في الفندق المختار'}
              </h3>
              <div>
                {!selectedHotel && <Button variant="outline" size="sm" onClick={() => setIsHotelDialogOpen(true)}>
                    <Plus className="w-4 h-4 ml-2" />
                    إضافة فندق غير موجود
                  </Button>}
                {selectedHotel && <Button variant="outline" size="sm" onClick={() => setSelectedHotel(null)}>
                    <X className="w-4 h-4 ml-2" />
                    تغيير الفندق
                  </Button>}
              </div>
            </div>
            
            {!selectedHotel ? <>
                <HotelFilters countries={countries} cities={cities} selectedCountry={selectedCountry} selectedCity={selectedCity} onCountryChange={setSelectedCountry} onCityChange={setSelectedCity} />
                <HotelsGrid hotels={filteredHotels} onSelectHotel={setSelectedHotel} />
              </> : <HotelStayDetails selectedHotel={selectedHotel} rooms={rooms} onAddHotelToPackage={handleAddHotelToPackage} />}
          </div>

          <div className="flex justify-between pt-6 border-t">
            <Button variant="outline" onClick={onPrev}>
              <ArrowRight className="w-4 h-4 ml-2" />
              السابق
            </Button>
            <Button onClick={onNext} className="bg-blue-600 hover:bg-blue-700">
              التالي
              <ArrowLeft className="w-4 h-4 mr-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
      <HotelDialog open={isHotelDialogOpen} onOpenChange={setIsHotelDialogOpen} editingHotel={null} onSubmit={handleSaveNewHotel} />
      
      {/* Progress Steps */}
      <ProgressSteps 
        currentStep={3} 
        completedSteps={completedSteps} 
        onStepClick={onStepClick} 
        isStepRequired={isStepRequired} 
      />
    </>;
};